import { useState, useEffect, useCallback, useMemo, useRef } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Calendar } from "@/components/ui/calendar";
import {
  LayoutDashboard,
  Scissors,
  Image,
  Star,
  CalendarDays,
  MessageSquare,
  LogOut,
  Plus,
  Trash2,
  Pencil,
  X,
  Save,
  Upload,
  Loader2,
  Bell,
  CheckCircle,
  Sparkles,
  Megaphone,
  ExternalLink,
  Copy,
  Eye,
  EyeOff,
  Send,
  Phone,
  Users,
  Mail,
  CalendarPlus,
  AlertTriangle,
  RotateCcw,
} from "lucide-react";
import { API, SITE_URL, authHeaders, resolveUrl, ImageUploadField } from "./shared";

const ServicesTab = () => {
  const [services, setServices] = useState([]);
  const [editing, setEditing] = useState(null);
  const [showAdd, setShowAdd] = useState(false);
  const [form, setForm] = useState({ name: "", description: "", duration: "", starting_price: 0, category: "braids", image_url: "" });

  const fetchServices = useCallback(async () => {
    const res = await axios.get(`${API}/services`);
    setServices(res.data);
  }, []);

  useEffect(() => { fetchServices(); }, [fetchServices]);

  const handleSave = async () => {
    try {
      if (editing) {
        await axios.put(`${API}/admin/services/${editing}`, form, authHeaders());
        toast.success("Service updated");
      } else {
        await axios.post(`${API}/admin/services`, { ...form, id: `svc-${Date.now()}` }, authHeaders());
        toast.success("Service added");
      }
      setEditing(null);
      setShowAdd(false);
      setForm({ name: "", description: "", duration: "", starting_price: 0, category: "braids", image_url: "" });
      fetchServices();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to save");
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this service?")) return;
    try {
      await axios.delete(`${API}/admin/services/${id}`, authHeaders());
      toast.success("Service deleted");
      fetchServices();
    } catch (err) {
      toast.error("Failed to delete");
    }
  };

  const startEdit = (svc) => {
    setEditing(svc.id);
    setForm({ name: svc.name, description: svc.description, duration: svc.duration, starting_price: svc.starting_price, category: svc.category, image_url: svc.image_url || "" });
    setShowAdd(true);
  };

  return (
    <div data-testid="admin-services-tab">
      <div className="flex items-center justify-between mb-6">
        <h2 className="font-serif text-2xl text-brand-text">Services</h2>
        <button
          onClick={() => { setShowAdd(!showAdd); setEditing(null); setForm({ name: "", description: "", duration: "", starting_price: 0, category: "braids", image_url: "" }); }}
          data-testid="add-service-btn"
          className="flex items-center gap-2 px-4 py-2 bg-brand-gold text-black font-sans text-sm font-semibold hover:bg-brand-gold-light transition-colors"
        >
          {showAdd ? <X size={14} /> : <Plus size={14} />}
          {showAdd ? "Cancel" : "Add Service"}
        </button>
      </div>

      {showAdd && (
        <div className="border border-brand-border bg-brand-surface p-5 mb-6 space-y-3" data-testid="service-form">
          <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Service name" data-testid="service-name-input" className="bg-brand-bg border-brand-border text-brand-text rounded-none" />
          <Textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} placeholder="Description" data-testid="service-desc-input" className="bg-brand-bg border-brand-border text-brand-text rounded-none" />
          <div className="grid grid-cols-2 gap-3">
            <Input value={form.duration} onChange={(e) => setForm({ ...form, duration: e.target.value })} placeholder="Duration (e.g., 4-6 hours)" data-testid="service-duration-input" className="bg-brand-bg border-brand-border text-brand-text rounded-none" />
            <Input type="number" value={form.starting_price} onChange={(e) => setForm({ ...form, starting_price: parseInt(e.target.value) || 0 })} placeholder="Price" data-testid="service-price-input" className="bg-brand-bg border-brand-border text-brand-text rounded-none" />
          </div>
          <Select value={form.category} onValueChange={(val) => setForm({ ...form, category: val })}>
            <SelectTrigger className="bg-brand-bg border-brand-border text-brand-text rounded-none" data-testid="service-category-select">
              <SelectValue placeholder="Category" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="braids">Braids</SelectItem>
              <SelectItem value="cornrows">Cornrows</SelectItem>
              <SelectItem value="twists">Twists</SelectItem>
              <SelectItem value="crochet">Crochet</SelectItem>
              <SelectItem value="locs">Faux Locs</SelectItem>
              <SelectItem value="mens">Men's</SelectItem>
              <SelectItem value="beauty">Hair & Beauty Services</SelectItem>
              <SelectItem value="other">Other</SelectItem>
            </SelectContent>
          </Select>
          <Input value={form.image_url} onChange={(e) => setForm({ ...form, image_url: e.target.value })} placeholder="Image URL" data-testid="service-image-input" className="bg-brand-bg border-brand-border text-brand-text rounded-none hidden" />
          <ImageUploadField currentUrl={form.image_url} onUrlChange={(url) => setForm({ ...form, image_url: url })} label="Service Image" />
          <button onClick={handleSave} data-testid="save-service-btn" className="flex items-center gap-2 px-6 py-2 bg-brand-gold text-black font-sans text-sm font-semibold hover:bg-brand-gold-light transition-colors">
            <Save size={14} /> {editing ? "Update" : "Add"} Service
          </button>
        </div>
      )}

      <div className="space-y-3">
        {services.map((svc) => (
          <div key={svc.id} className="flex items-center gap-4 p-4 border border-brand-border bg-brand-surface" data-testid={`admin-service-${svc.id}`}>
            {svc.image_url && <img src={resolveUrl(svc.image_url)} alt={svc.name} className="w-16 h-16 object-cover flex-shrink-0" />}
            <div className="flex-1 min-w-0">
              <p className="font-sans text-sm font-medium text-brand-text">{svc.name}</p>
              <p className="font-sans text-xs text-brand-text-muted">{svc.duration} &middot; From ${svc.starting_price}</p>
            </div>
            <div className="flex gap-2 flex-shrink-0">
              <button onClick={() => startEdit(svc)} data-testid={`edit-service-${svc.id}`} className="p-2 border border-brand-border text-brand-text-muted hover:text-brand-gold hover:border-brand-gold transition-colors">
                <Pencil size={14} />
              </button>
              <button onClick={() => handleDelete(svc.id)} data-testid={`delete-service-${svc.id}`} className="p-2 border border-brand-border text-brand-text-muted hover:text-red-400 hover:border-red-400 transition-colors">
                <Trash2 size={14} />
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default ServicesTab;
